import axios from 'axios';

// 初始基础地址：优先环境变量。
// - 开发环境：默认 http://localhost:3003/api
// - 生产环境：默认走同源反代 /api
const isDev = process.env.NODE_ENV === 'development';
const initialBase = process.env.REACT_APP_API_BASE || (isDev ? 'http://localhost:3003/api' : '/api');

const STORAGE_KEY = 'HRMS_API_BASE';

const getStoredBase = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
};

const setStoredBase = (base) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (!base) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, base);
  } catch (e) {
    // ignore
  }
};

// 纠错：修复被错误回退逻辑污染的地址，例如：
//   http://host:3004/hrms/hrms/hrms/api  -> http://host:3004/hrms/api
//   /hrms/hrms/api -> /hrms/api
const sanitizeApiBase = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (s.startsWith('/')) {
    const p = s.replace(/\/+$/g, '');
    if (/^\/(?:hrms\/)+api$/i.test(p)) return '/hrms/api';
    return p;
  }
  try {
    const u = new URL(s);
    const p = String(u.pathname || '').replace(/\/+$/g, '');
    if (/^\/(?:hrms\/)+api$/i.test(p)) u.pathname = '/hrms/api';
    return u.toString().replace(/\/+$/g, '');
  } catch (e) {
    return s.replace(/\/+$/g, '');
  }
};

const __storedBaseRaw = getStoredBase() || '';
const __storedBaseSanitized = sanitizeApiBase(__storedBaseRaw);
// 运行期可能根据 404 回退 / 或用户选择服务器
let dynamicBase = __storedBaseSanitized || initialBase;
// 若发现本地已保存的地址被污染（/hrms 重复叠加），启动时自动纠正并回写。
try {
  if (__storedBaseRaw && __storedBaseSanitized && __storedBaseSanitized !== __storedBaseRaw) {
    setStoredBase(__storedBaseSanitized);
  }
} catch (e) {
  // ignore
}

const isLoopbackHost = (h) => {
  const host = String(h || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
};

const isLoopbackBase = (base) => {
  const b = String(base || '').trim().toLowerCase();
  if (!b) return false;
  // relative base is not loopback
  if (b.startsWith('/')) return false;
  try {
    const u = new URL(b);
    return isLoopbackHost(u.hostname);
  } catch (e) {
    return false;
  }
};

// 生产环境保护：如果页面不是在本机打开，却把 API 指到了 localhost/127，会必然连不上（指向用户自己电脑）
try {
  const host = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';
  const pageIsLoopback = isLoopbackHost(host);
  if (!isDev && !pageIsLoopback && isLoopbackBase(dynamicBase)) {
    console.warn('[api] ignored loopback api base override in non-loopback page', { dynamicBase, host });
    setStoredBase('');
    dynamicBase = initialBase;
  }
} catch (e) {
  // ignore
}

const api = axios.create({
  baseURL: dynamicBase,
  timeout: 15000,
  withCredentials: true,
});

export const getApiBase = () => dynamicBase;
// 将相对 API 路径（/api/... 或 /hrms/api/...）补全为“登录页设置的后端地址”。
// 目的：本地开发时可直连远端后端，不依赖 CRA proxy（localhost:3004）。
export const resolveApiUrl = (rawUrl) => {
  const u = String(rawUrl || '').trim();
  if (!u) return '';
  if (u.startsWith('blob:') || u.startsWith('data:')) return u;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(u) || u.startsWith('//')) return u; // absolute

  const baseTrim = String(dynamicBase || '').trim().replace(/\/+$/g, '');
  if (!baseTrim) return u;

  const isApiPath = u === '/api' || u.startsWith('/api/') || u === '/hrms/api' || u.startsWith('/hrms/api/');
  if (!isApiPath) return u;

  const rest = u.startsWith('/hrms/api') ? u.slice('/hrms/api'.length) : u.slice('/api'.length);
  return `${baseTrim}${rest}`;
};

export const setApiBase = (base) => {
  const next = sanitizeApiBase(base);
  if (!next) return;
  dynamicBase = next;
  api.defaults.baseURL = dynamicBase;
  setStoredBase(dynamicBase);
};

// 仅本次运行期生效，不写入 localStorage
export const setApiBaseRuntime = (base) => {
  const next = sanitizeApiBase(base);
  if (!next) return;
  dynamicBase = next;
  api.defaults.baseURL = dynamicBase;
};

export const clearApiBaseOverride = () => {
  dynamicBase = initialBase;
  api.defaults.baseURL = dynamicBase;
  setStoredBase('');
};

// 请求拦截：自动注入 Authorization（若未显式传入）
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token && !cfg.headers.Authorization) {
    cfg.headers.Authorization = 'Bearer ' + token;
  }
  return cfg;
});

// 响应拦截：统一错误消息与 401 处理提示
api.interceptors.response.use(
  r => r,
  async e => {
    const status = e?.response?.status;
    const cfg = e?.config;
    
    // 回退策略：仅处理常见的 /api 与 /hrms/api 兼容。
    // 注意：生产环境通常只有 /hrms/api 由 Nginx 反代到后端；若错误回退到 /api，可能命中前端 index.html（200），
    // 进而导致前端把 HTML 当 JSON 使用并崩溃（表现为空白页）。
    try {
      const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';
      const isRelativeBase = String(dynamicBase || '').trim().startsWith('/');
      const allowFallback = isDev || host === 'localhost' || host === '127.0.0.1' || isRelativeBase;

      if (allowFallback && (status === 404 || status === 502) && cfg && !cfg.__retried) {
        // 如果当前是 /api，则回退到 /hrms/api；反之亦然（主要用于历史路径差异）
        // 注意：不能对绝对地址做简单的 string replace('/api','/hrms/api')，否则会把 /hrms/api 污染成 /hrms/hrms/api。
        const toggleApiBase = (base) => {
          const b = sanitizeApiBase(base);
          if (!b) return null;
          if (b.startsWith('/')) {
            if (b === '/api' || b.startsWith('/api/')) return '/hrms/api';
            if (b === '/hrms/api' || b.startsWith('/hrms/api/')) return '/api';
            return null;
          }
          try {
            const u = new URL(b);
            const p = String(u.pathname || '').replace(/\/+$/g, '');
            if (p === '/hrms/api' || p.endsWith('/hrms/api')) {
              u.pathname = p.replace(/\/hrms\/api$/i, '/api');
              return u.toString().replace(/\/+$/g, '');
            }
            if (p === '/api' || p.endsWith('/api')) {
              // 仅替换末尾的 /api
              u.pathname = p.replace(/\/api$/i, '/hrms/api');
              return u.toString().replace(/\/+$/g, '');
            }
            return null;
          } catch (err) {
            return null;
          }
        };

        const fallbackBase = toggleApiBase(dynamicBase);

        if (fallbackBase && dynamicBase !== fallbackBase) {
          console.warn(`[api] 404 error, retrying request against ${fallbackBase}`);
          dynamicBase = fallbackBase;
          api.defaults.baseURL = dynamicBase;
          cfg.__retried = true;
          cfg.baseURL = dynamicBase;
          return api(cfg);
        }
      }
    } catch (retryErr) {
      // 重试失败，继续正常的错误处理
      console.error('[api] retry error', retryErr);
    }
    
    // 统一错误消息处理
    if (status === 401) {
      e.message = '未授权或登录已过期，请重新登录';
    } else if (e?.response?.data?.message) {
      e.message = e.response.data.message;
    } else if (e?.response?.data?.error) {
      // 兼容后端返回 { error: '...' }
      e.message = e.response.data.error;
    } else if (typeof e?.response?.data === 'string') {
      // 兼容 Nginx/网关返回的 HTML/plaintext 错误
      const text = e.response.data;
      if (text.includes('Request Entity Too Large') || text.includes('413')) {
        e.message = '请求体过大（413）。请压缩上传文件或联系管理员放开网关/反代上传限制。';
      }
    } else if (!e.message && status) {
      e.message = '请求失败，状态码 ' + status;
    }
    
    // 允许调用方标记静默请求（例如：无权限时探测 settings/systemName）
    if (!cfg?.__silent) {
      console.error('[api] error', status, e.message, 'url:', cfg?.url);
    }
    return Promise.reject(e);
  }
);

// 生产/部署环境自动探测：
// - 若同源 /hrms/api 没有反代（404），尝试 http(s)://<host>:3004/api
// - 若用户持久化选择了 :3003 且不可用，自动切到 :3004（若可用）
// 只在浏览器环境运行一次，不影响开发环境。
const probeApiBaseOnce = async () => {
  if (typeof window === 'undefined' || !window.location) return;
  if (isDev) return;

  const host = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';

  const fetchJsonWithTimeout = async (url, timeoutMs) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const resp = await fetch(url, { method: 'GET', credentials: 'include', signal: ctrl.signal });
      if (!resp.ok) return null;
      const data = await resp.json().catch(() => null);
      return data;
    } finally {
      clearTimeout(t);
    }
  };

  const candidates = [
    dynamicBase,
    '/hrms/api',
    `${protocol}//${host}:3004/api`,
    `${protocol}//${host}:3003/api`,
  ];

  const uniq = Array.from(new Set(candidates.map(s => String(s || '').trim()).filter(Boolean)));

  const check = async (base) => {
    const healthUrl = base.startsWith('/')
      ? `${base.replace(/\/$/, '')}/health`
      : `${base.replace(/\/$/, '')}/health`;
    try {
      const data = await fetchJsonWithTimeout(healthUrl, 2500);
      return data && data.ok === true;
    } catch (e) {
      return false;
    }
  };

  // 仅在“已持久化 3003 且明显不可用，但 3004 可用”时自动纠正持久化
  const stored = getStoredBase();
  const storedLooks3003 = !!stored && /:\s*3003\b/.test(stored);
  if (storedLooks3003) {
    const ok3003 = await check(stored);
    if (!ok3003) {
      const alt3004 = `${protocol}//${host}:3004/api`;
      const ok3004 = await check(alt3004);
      if (ok3004) {
        console.warn('[api] detected invalid stored base (3003), switching to 3004', { stored, alt3004 });
        setApiBase(alt3004);
        return;
      }
    }
  }

  // 未持久化时：找到第一个可用的候选作为“运行期 base”
  if (!stored) {
    for (const base of uniq) {
      if (await check(base)) {
        // 不写入 localStorage，避免误覆盖用户配置
        if (dynamicBase !== base) {
          console.info('[api] auto selected api base', base);
          setApiBaseRuntime(base);
        }
        break;
      }
    }
  }
};

// fire-and-forget
probeApiBaseOnce();

export default api;
